import { useCallback, type FC } from 'react';
import { generateMonthButtons } from '../EventCalendarHelpers';
import type { EventCalendarData } from 'cms/resolvers/eventCalendarResolver';

interface EventNavBarProps {
    data: EventCalendarData;
}

const EventNavBar: FC<EventNavBarProps> = ({ data }) => {
    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>): void => {
        if ((event as unknown as KeyboardEvent).key === 'Enter') {
            const monthKey = (event.currentTarget as HTMLButtonElement).dataset.month;
            if (!monthKey) {
                return;
            }
            const monthSection = document.getElementById(monthKey); // Target the event-calendar-month section
            if (monthSection) {
                const eventNavBar = document.querySelector('.event-nav-bar') as HTMLElement;
                const navBarHeight = eventNavBar ? eventNavBar.offsetHeight : 0;
                const targetPosition = monthSection.getBoundingClientRect().top + window.scrollY - navBarHeight;
                window.scrollTo({
                    top: targetPosition,
                    behavior: 'smooth',
                });
            }
            // Restore support logic for keyboard users
            const headerEl = document.getElementById(`${monthKey}_header`);
            if (headerEl) {
                const support = headerEl.querySelector('.tab-support');
                if (support) {
                    support.classList.add('show');
                }

                let focusTimeout: NodeJS.Timeout;
                const onScroll = (): void => {
                    if (focusTimeout) {
                        clearTimeout(focusTimeout);
                    }
                    focusTimeout = setTimeout(() => {
                        (headerEl as HTMLElement).focus();
                        window.removeEventListener('scroll', onScroll);
                    }, 100);
                };
                window.addEventListener('scroll', onScroll);
            }
        }
    }, []);

    const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>): void => {
        const monthKey = (event.currentTarget as HTMLButtonElement).dataset.month;
        if (!monthKey) {
            return;
        }
        const monthSection = document.getElementById(monthKey); // Target the event-calendar-month section
        if (monthSection) {
            const eventNavBar = document.querySelector('.event-nav-bar') as HTMLElement;
            const navBarHeight = eventNavBar ? eventNavBar.offsetHeight : 0;
            const targetPosition = monthSection.getBoundingClientRect().top + window.scrollY - navBarHeight;
            window.scrollTo({
                top: targetPosition,
                behavior: 'smooth',
            });
        }
    }, []);

    return (
        <nav className="event-nav-bar" role="navigation" aria-label="Month Selector">
            <ul>
                {generateMonthButtons(data, handleClick, handleKeyDown)}
            </ul>
        </nav>
    );
};

export default EventNavBar;
